import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { ChevronRight, ChevronLeft, Building2, Globe, CalendarDays, Clock, Flag, GraduationCap, MapPin } from "lucide-react";
import ProgressBar from "@/components/ProgressBar";
import { INDIAN_STATES } from "@/lib/indianStates";
import { toast } from "sonner";

const STAGES = [
  { v: "not_started",       label: "Not started",              hint: "You're just starting to look." },
  { v: "admission_letter",  label: "Admission letter in hand", hint: "You're admitted; visa comes next." },
  { v: "visa_applied",      label: "Visa applied",             hint: "You've submitted; waiting on decision." },
  { v: "visa_approved",     label: "Visa approved",            hint: "You have your visa. On to arrival." },
];

export default function Intake() {
  const nav = useNavigate();
  const [countries, setCountries] = useState([]);
  const [bschools, setBschools] = useState([]);
  const [partners, setPartners] = useState([]);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    home_university: "",
    host_university: "",
    host_city: "",
    country: "",
    duration_days: "",
    start_date: "",
    process_stage: "",
    // iter48 — optional home-origin fields powering the pre-departure Manifest tier.
    home_city:  "",
    home_state: "",
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/countries").then(r => setCountries(r.data.countries)).catch(()=>{});
    api.get("/bschools").then(r => setBschools(r.data.bschools)).catch(()=>{});
  }, []);

  // fetch partners when home school changes
  useEffect(() => {
    if (form.home_university && !form.home_university.startsWith("__other__")) {
      const school = bschools.find(b => b.short === form.home_university);
      if (school) {
        api.get(`/bschools/${encodeURIComponent(school.short)}/partners`).then(r => setPartners(r.data.partners)).catch(()=>setPartners([]));
      }
    } else {
      setPartners([]);
    }
  }, [form.home_university, bschools]);

  const steps = useMemo(() => [
    { key: "home_university", icon: Building2,     label: "Home university",                                    type: "school" },
    { key: "host_university", icon: GraduationCap, label: "Host university",                                    type: "partner" },
    { key: "country",         icon: Globe,         label: "Host country",                                       type: "select" },
    { key: "duration_days",   icon: Clock,         label: "Duration in days (not weeks or months)", type: "number", placeholder: "e.g. 120" },
    { key: "start_date",      icon: CalendarDays,  label: "Start date",                                         type: "date" },
    { key: "process_stage",   icon: Flag,          label: "Where are you in the process?",                       type: "stage" },
    { key: "home_origin",     icon: MapPin,        label: "Where in India are you flying from? (optional)",     type: "home_origin", optional: true },
  ], []);

  const cur = steps[step];
  const total = steps.length;

  useEffect(() => {
    if (form.country && form.duration_days && !Number.isNaN(+form.duration_days)) {
      api.get(`/rules/preview?country=${encodeURIComponent(form.country)}&duration_days=${form.duration_days}`)
        .then(r => setPreview(r.data)).catch(()=>setPreview(null));
    } else setPreview(null);
  }, [form.country, form.duration_days]);

  const setVal = (v) => setForm({ ...form, [cur.key]: v });

  const canNext = () => {
    const v = form[cur.key];
    if (cur.optional) return true;  // pre-departure step is always advanceable
    if (cur.key === "duration_days") return v && Number(v) > 0 && Number(v) <= 730;
    if (cur.key === "home_university" && String(v).startsWith("__other__")) {
      return String(v).replace(/^__other__:/,"").trim().length > 0;
    }
    return String(v || "").trim().length > 0;
  };
  const next = () => setStep(Math.min(total - 1, step + 1));
  const back = () => setStep(Math.max(0, step - 1));

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      // Referral capture — ?ref=CODE on the URL (from /start?ref=CODE deep-link)
      // is persisted in sessionStorage so it survives login/signup detours.
      const url = new URL(window.location.href);
      const refFromUrl = url.searchParams.get("ref");
      if (refFromUrl) sessionStorage.setItem("moe_ref", refFromUrl.toUpperCase());
      const ref = sessionStorage.getItem("moe_ref") || null;
      await api.post("/cases", { ...form, duration_days: Number(form.duration_days), ref });
      if (ref) sessionStorage.removeItem("moe_ref");
      toast.success("Case created. Timeline generated.");
      nav("/dashboard");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 sm:pt-10 md:pt-16 pb-16 md:pb-24">
      <div className="mb-5 sm:mb-6">
        <div className="text-xs uppercase tracking-widest text-muted font-semibold">Intake · {step + 1} of {total}</div>
        <div className="mt-3"><ProgressBar value={step + 1} max={total} /></div>
      </div>

      <div key={cur.key} className="moe-fade-up moe-card p-5 sm:p-6 md:p-10">
        <div className="flex items-center gap-3 mb-5 sm:mb-6">
          <cur.icon size={22} className="text-elec shrink-0" strokeWidth={2} />
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight leading-tight">{cur.label}</h2>
        </div>

        {cur.type === "school" ? (
          <div className="space-y-3">
            <div className="moe-field">
              <select data-testid={TID.intakeHome} value={form.home_university.startsWith("__other__") ? "__other__" : form.home_university} onChange={(e)=>{
                if (e.target.value === "__other__") setForm({...form, home_university: "__other__:"});
                else setForm({...form, home_university: e.target.value});
              }}>
                <option value="">Select your school…</option>
                {bschools.map(b => <option key={b.short} value={b.short}>{b.short} · {b.city}</option>)}
                <option value="__other__">My school isn't listed (type below)</option>
              </select>
              <label>Home university</label>
            </div>
            {form.home_university.startsWith("__other__") && (
              <div className="moe-field">
                <input
                  data-testid="intake-home-other"
                  autoFocus
                  value={form.home_university.replace(/^__other__:/,"")}
                  onChange={(e)=>setForm({...form, home_university: `__other__:${e.target.value}`})}
                  placeholder=" "
                />
                <label>Enter your school name</label>
              </div>
            )}
          </div>
        ) : cur.type === "partner" ? (
          <>
            {partners.length > 0 ? (
              <div className="moe-field">
                <select data-testid={TID.intakeHost} value={form.host_university} onChange={(e)=>{
                  setVal(e.target.value);
                  const p = partners.find(x => x.partner_name === e.target.value);
                  if (p) setForm(f => ({ ...f, host_university: e.target.value, host_city: p.partner_city, country: p.partner_country }));
                }}>
                  <option value="">Pick from your school's partner list…</option>
                  {partners.map(p => <option key={p.id} value={p.partner_name}>{p.partner_name} · {p.partner_city}, {p.partner_country}</option>)}
                </select>
                <label>Host university (from your school's MoUs)</label>
              </div>
            ) : (
              <div className="moe-field">
                <input data-testid={TID.intakeHost} required value={form.host_university} onChange={(e)=>setVal(e.target.value)} placeholder=" "/>
                <label>Host university</label>
              </div>
            )}
          </>
        ) : cur.type === "select" ? (
          <div className="moe-field">
            <select data-testid={TID.intakeCountry} value={form.country} onChange={(e) => setVal(e.target.value)}>
              <option value="">Select a country…</option>
              {countries.map((c) => <option key={c.country_name} value={c.country_name}>{c.country_name}</option>)}
            </select>
            <label>Country</label>
          </div>
        ) : cur.type === "home_origin" ? (
          <div className="space-y-3">
            <p className="-mt-1 text-[13px] text-muted leading-relaxed">
              Powers pre-departure matching. Connect with students from your city or
              consular jurisdiction heading to the same country in the same window.{" "}
              <span className="text-ink font-semibold">Optional</span>. You can skip and add it later.
            </p>
            <div className="moe-field">
              <input
                data-testid="intake-home-city"
                type="text"
                value={form.home_city}
                onChange={(e) => setForm({ ...form, home_city: e.target.value })}
                placeholder=" "
              />
              <label>Home city (e.g. Mumbai)</label>
            </div>
            <div className="moe-field">
              <select
                data-testid="intake-home-state"
                value={form.home_state}
                onChange={(e) => setForm({ ...form, home_state: e.target.value })}
              >
                <option value="">Select your home state / UT (optional)…</option>
                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <label>Home state / UT</label>
            </div>
          </div>
        ) : cur.type === "stage" ? (
          <div className="grid gap-2">
            {STAGES.map((s) => (
              <button key={s.v} data-testid={`${TID.intakeStage}-${s.v}`} type="button" onClick={() => setVal(s.v)}
                className={`text-left p-4 rounded-xl border transition-all ${form.process_stage === s.v ? "border-ink bg-[color:var(--moe-mint)]" : "border-ink/40 hover:bg-[color:var(--moe-mint)]/30"}`}>
                <div className="font-semibold text-ink">{s.label}</div>
                <div className="text-xs text-muted mt-0.5">{s.hint}</div>
              </button>
            ))}
          </div>
        ) : (
          <div className="moe-field">
            <input
              data-testid={
                cur.key === "duration_days" ? TID.intakeDuration :
                cur.key === "start_date" ? TID.intakeStart : `intake-${cur.key}`
              }
              type={cur.type || "text"}
              value={form[cur.key]}
              onChange={(e) => setVal(e.target.value)}
              placeholder=" "
              min={cur.type === "number" ? 1 : undefined}
              max={cur.type === "number" ? 730 : undefined}
            />
            <label>{cur.placeholder || cur.label}</label>
          </div>
        )}

        {preview && (cur.key === "duration_days" || cur.key === "process_stage" || cur.key === "start_date") && (
          <div className="mt-6 moe-panel-cyan p-4">
            <div className="text-[11px] uppercase tracking-widest font-bold">Visa path preview</div>
            <div className="mt-1 text-lg font-bold">{preview.visa_type}</div>
            <div className="mt-1 text-xs text-ink/80">{preview.message}</div>
            {preview.severity === "mandatory" && <div className="mt-2 moe-chip moe-chip-pink">Mandatory long-stay</div>}
            {preview.severity === "warn" && <div className="mt-2 moe-chip moe-chip-yolk">Warning · apply long-stay</div>}
          </div>
        )}

        {err && <div className="mt-4 text-sm text-red-600">{err}</div>}

        <div className="mt-6 sm:mt-8 flex items-center justify-between gap-3">
          <button data-testid={TID.intakeBack} onClick={back} disabled={step === 0}
            className="moe-btn moe-btn-ghost !px-3 sm:!px-4 disabled:opacity-30 shrink-0">
            <ChevronLeft size={16}/> <span className="hidden sm:inline">Back</span>
          </button>
          {step < total - 1 ? (
            <button data-testid={TID.intakeNext} onClick={next} disabled={!canNext()}
              className="moe-btn moe-btn-primary !px-4 sm:!px-5 disabled:opacity-40 shrink-0">
              Next <ChevronRight size={16}/>
            </button>
          ) : (
            <button data-testid={TID.intakeSubmit} onClick={submit} disabled={!canNext() || busy}
              className="moe-btn moe-btn-primary !px-4 sm:!px-5 disabled:opacity-40 shrink-0">
              {busy ? "Building…" : "Build my plan"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
